import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { NotificationAction } from '../graphql/__types__/CreateContentMentionNotificationAction';
import { CreateInlineTaskNotificationMutation } from '../graphql/CreateInlineTaskNotificationMutation.graphql';

type CreateInlineTaskNotificationMutationProps = {
	contentId: string;
	setError: React.Dispatch<React.SetStateAction<Error | null>>;
	showDontNotifyFailedFlag: () => void;
};

export type UpdatedTaskItem = {
	operation: string;
	recipientAccountId: string;
	taskId: string;
	notificationAction: NotificationAction;
	recipientMentionLocalId: string;
};

type SendCreateInlineTaskNotificationsProps = {
	tasks?: UpdatedTaskItem[];
};

export const useCreateInlineTaskNotificationMutation = ({
	contentId,
	setError,
	showDontNotifyFailedFlag,
}: CreateInlineTaskNotificationMutationProps) => {
	const [createInlineTaskNotificationMutation, { error: createTaskNotificationError }] =
		useMutation(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
			CreateInlineTaskNotificationMutation,
		);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendCreateInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'createInlineTaskMention',
						attributes: {
							contentId,
							mentions: tasks?.map((task) => ({
								taskId: task.taskId,
								mentionId: task.recipientMentionLocalId,
								mentionedUserId: task.recipientAccountId,
							})),
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			setTimeout(() => {
				createInlineTaskNotificationMutation({
					variables: { contentId, tasks },
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch((error) => {
						fireAnalyticsEvent('failed');
						setError(error);
					});
			}, 15000);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createAnalyticsEvent, createInlineTaskNotificationMutation],
	);

	const cancelInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps) => {
			createInlineTaskNotificationMutation({
				variables: { contentId, tasks },
			}).catch(() => {
				showDontNotifyFailedFlag();
			});
		},
		[contentId, createInlineTaskNotificationMutation, showDontNotifyFailedFlag],
	);

	return {
		sendCreateInlineTaskNotifications,
		cancelInlineTaskNotifications,
		createTaskNotificationError,
	};
};
